import { createSlice } from '@reduxjs/toolkit'
import { api } from 'app/services/api'
import { isTaxonomyId } from 'helpers'
import { TaxonomyId, TermId } from 'types'

export type SettingsState = {
  status: 'idle' | 'loading' | 'failed'
  baseUri: string
  settingsLoaded: boolean
  includedTaxonomies: TaxonomyId[]
  taxonomySlugs: { [P in TaxonomyId]?: string }
  includedCountries: TermId[]
  globalCopy: {
    stockistsDesc: string
    stockistsHeading: string
  }
  pacificSticker: {
    sourceUrl: string
  }
  pngSticker: {
    sourceUrl: string
  }
}

const initialState: SettingsState = {
  status: 'idle',
  baseUri: '/',
  settingsLoaded: false,
  includedTaxonomies: [],
  taxonomySlugs: {},
  includedCountries: [],
  globalCopy: {
    stockistsDesc: '',
    stockistsHeading: '',
  },
  pacificSticker: {
    sourceUrl: ''
  },
  pngSticker: {
    sourceUrl: ''
  },
}

const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addMatcher(api.endpoints.settings.matchPending, (state) => {
        state.status = 'loading'
      })
      .addMatcher(api.endpoints.settings.matchRejected, (state) => {
        state.status = 'failed'
      })
      .addMatcher(api.endpoints.settings.matchFulfilled, (state, { payload }) => {
        state.baseUri = payload.productTypeSettings?.uri || '/'

        const includedTaxonomies = (payload.allSettings?.searchSettings?.taxonomies || []).reduce(
          (taxIds, value) => (isTaxonomyId(value) ? taxIds.concat(value) : taxIds),
          [] as TaxonomyId[]
        )

        // We always need these
        includedTaxonomies.concat('PRODUCTCATEGORY')

        const includedCountries = (payload.allSettings?.searchSettings?.countries || []).reduce(
          (termIds, value) => (value?.databaseId ? termIds.concat(value?.databaseId) : termIds),
          [] as TermId[]
        )
        state.taxonomySlugs = (payload.taxonomies?.nodes || []).reduce((results, item) => {
          const { slug, enumValue } = item ?? {}
          if (isTaxonomyId(enumValue)) results[enumValue] = slug ?? ''
          return results
        }, {} as SettingsState['taxonomySlugs'])
        state.includedTaxonomies = includedTaxonomies
        state.includedCountries = includedCountries
        state.globalCopy.stockistsDesc = payload.allSettings?.searchSettings?.stockistsDesc || ''
        state.globalCopy.stockistsHeading = payload.allSettings?.searchSettings?.stockistsHeading || ''
        state.pacificSticker.sourceUrl = payload.allSettings?.searchSettings?.pacificSticker?.sourceUrl || ''
        state.pngSticker.sourceUrl = payload.allSettings?.searchSettings?.pngSticker?.sourceUrl || ''
        state.settingsLoaded = true
      })
  },
})
export default settingsSlice
